import React, { useState, useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { LogOut, Menu, User, X, LayoutDashboard, GraduationCap, Users, UserRound, CalendarCheck, MessageSquare, Building2, GraduationCapIcon, Banknote } from 'lucide-react';
import MenuItem from './MenuItem';
import { useAuth } from '../auth/AuthProvider';
import { useTranslation } from 'react-i18next';

const Layout = () => {
  const { t, i18n } = useTranslation('layout');
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1024);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const sidebarRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const { user, logout } = useAuth();
  const isRTL = i18n.language === 'ar';
  
  // Define role-specific menu configurations
  const getMenuItemsByRole = () => {
    // Common menu items for all users
    const commonItems = [
      {
        title: t('dashboard'),
        path: '/',
        icon: <LayoutDashboard className="w-5 h-5" />
      },
    ];
    
    // Define additional menu items based on role
    switch(user?.role) {
      case 'super_admin':
        return [
          ...commonItems,
          {
            title: t('school.title'),
            icon: <Building2 className="w-5 h-5" />,
            submenu: [
              { title: t('school.registerSchool'), path: '/register-school' },
              { title: 'All Schools', path: '/all-schools' }
            ]
          },
        ];
        
      case 'school_admin':
        return [
          ...commonItems,
          {
            title: t('teachers.title'),
            icon: <GraduationCap className="w-5 h-5" />,
            submenu: [
              { title: t('teachers.allTeachers'), path: '/all-teachers' },
              { title: t('teachers.addTeacher'), path: '/register-teacher' },
            ]
          },
          {
            title: t('students.title'),
            icon: <Users className="w-5 h-5" />,
            submenu: [
              { title: t('students.allStudents'), path: '/all-students' },
              { title: t('students.addStudent'), path: '/register-student' }
            ]
          },
          {
            title: t('parents.title'),
            icon: <UserRound className="w-5 h-5" />,
            submenu: [
              { title: t('parents.allParents'), path: '/all-parents' }
            ]
          },
          {
            title: t('attendance.title'),
            icon: <CalendarCheck className="w-5 h-5" />,
            submenu: [
              { title: t('attendance.markAttendance'), path: '/mark-attendance' },
              { title: t('attendance.seeAttendance'), path: '/attendance-report' }
            ]
          },
          {
            title: t('exams.title'),
            icon: <GraduationCapIcon className="w-5 h-5" />,
            submenu: [
              { title: t('exams.examManagement'), path: '/exam-management' },
              { title: t('exams.gradingScales'), path: '/grading-scales' },
              { title: t('exams.markEntry'), path: '/mark-entry' },
              { title: t('exams.rawScores'), path: '/exam-raw-scores' },
              { title: t('exams.rawMarksheet'), path: '/raw-mark-sheet' },
              { title: t('exams.combineExams'), path: '/exams-combination' },
              { title: t('exams.performanceAnalysis'), path: '/exan-merit-list' },
              { title: t('exams.reportCards'), path: '/report-card-generator' },
            ]
          },
          {
            title: t('fees.title'),
            icon: <Banknote className="w-5 h-5" />,
            submenu: [
              { title: t('fees.allFeeStructures'), path: "/fees-structure"},
              { title: "Bill Management", path: "/billmanagement"},
              { title: "Make Payment", path: "/payment"},
              { title: "Fee Dashboard", path: "/fees-dashboard"}
            ]
          },
          {
            title: t('messaging.title'),
            icon: <MessageSquare className="w-5 h-5" />,
            submenu: [
              { title: t('messaging.sms'), path: '/send-sms' },
              { title: t('messaging.email'), path: '/send-email' }
            ]
          },
          {
            title: t('school.title'),
            icon: <Building2 className="w-5 h-5" />,
            submenu: [
              { title: t('school.profile'), path: '/school-profile' },
              { title: t('school.sessionManagement'), path: '/session-management' },
              { title: t('school.subjects'), path: '/subjects' },
              { title: t('school.subjectCategories'), path: '/subject-categories' },
              { title: t('school.academicYear'), path: '/academic-year-management' }
            ]
          },
        ];
        
      case 'teacher':
        return [
          ...commonItems,
          {
            title: t('students.title'),
            icon: <Users className="w-5 h-5" />,
            path: '/all-students'
          },
          {
            title: t('attendance.title'),
            icon: <CalendarCheck className="w-5 h-5" />,
            submenu: [
              { title: t('attendance.markAttendance'), path: '/mark-attendance' },
              { title: t('attendance.seeAttendance'), path: '/attendance-report' }
            ]
          },
          {
            title: t('school.title'),
            icon: <Building2 className="w-5 h-5" />,
            path: '/school-teacher-profile',
          },
        ];
        
      case 'parent':
        return [
        ];
        
      default:
        return commonItems;
    }
  };

  // Get menu items based on user role
  const menuItems = getMenuItemsByRole();

  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        window.innerWidth < 1024) {
        setSidebarOpen(false);
      }

      if (profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNavigate = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
  };

  const sidebarPositionClass = isRTL ? 'right-0' : 'left-0';
  const sidebarTransformClass = isRTL 
    ? `${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}`
    : `${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`;
  const mainMarginClass = isRTL ? 'lg:mr-64' : 'lg:ml-64';
  const menuButtonAlignmentClass = isRTL ? 'ml-auto mr-4' : 'mr-auto ml-4';
  const dropdownAlignmentClass = isRTL ? 'left-0' : 'right-0';

  return (
    <div className="flex min-h-screen bg-gray-50 w-full overflow-hidden">
      <aside
        ref={sidebarRef}
        className={`fixed top-0 ${sidebarPositionClass} h-full w-64 bg-[#0E5252] text-white transform 
          ${sidebarTransformClass} transition-transform duration-300 ease-in-out lg:translate-x-0 z-50 flex flex-col
          shadow-lg`}
      >
        <div className="absolute top-0 left-0 right-0 bg-[#0E5252] border-b border-[#106363] z-10">
          <div className="flex items-center justify-between h-16 px-4">
            <h1 className="text-center text-2xl font-bold tracking-wider text-white">YOVENTA</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-md lg:hidden text-white hover:bg-[#106363] transition-colors duration-200"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto mt-16 pt-4 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
          {menuItems.map((item, index) => (
            <MenuItem key={index} item={item} onNavigate={handleNavigate} />
          ))}
        </nav>
      </aside>

      <div className={`flex-1 w-full ${mainMarginClass} flex flex-col h-screen overflow-hidden`}>
        <header className="sticky top-0 z-40 h-16 bg-white border-b shadow-sm flex-shrink-0">
          <div className="flex items-center justify-between h-full px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className={`p-2 rounded-md lg:hidden hover:bg-gray-100 transition-colors duration-200 ${menuButtonAlignmentClass}`}
            >
              <Menu className="w-6 h-6" />
            </button>
            
            <div className={`flex items-center gap-3 ${isRTL ? 'mr-auto' : 'ml-auto'}`}>
              <button
                onClick={() => i18n.changeLanguage(i18n.language === 'en' ? 'ar' : 'en')}
                className="flex items-center px-3 py-2 text-sm text-white bg-[#0E5252] hover:bg-[#106363] rounded-md transition-colors duration-200"
              >
                {t(`languageSwitch.${i18n.language === 'en' ? 'ar' : 'en'}`)}
              </button>
              
              <div className="relative" ref={profileDropdownRef}>
                <button
                  onClick={toggleProfileDropdown}
                  className={`flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} p-2 rounded-md hover:bg-gray-100 transition-colors duration-200`}
                >
                  <div className="p-1 bg-[#0E5252] rounded-full">
                    <User className="w-5 h-5 text-white" />
                  </div>
                </button>
                
                {profileDropdownOpen && (
                  <div className={`absolute ${dropdownAlignmentClass} mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200`}>
                    <div className="px-4 py-2 border-b">
                      <div className="text-sm text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</div>
                    </div>
                    {/* <a href="/account" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      {t('account')}
                    </a> */}
                    <button
                      onClick={logout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <LogOut className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        {t('logout')}
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
        
        <main className="flex-1 overflow-auto p-4 sm:p-6 w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;